import { api } from '@/api/client';
import { Button } from '@/components/ui';
import { useTranslation } from '@kgc/i18n';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, toast } from '@kgc/ui';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { CronPicker } from '../components/CronPicker';
import type { PublishedWorkflow } from '../types';

interface ScheduleWorkflowDialogProps {
  workflow: PublishedWorkflow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function ScheduleWorkflowDialog({
  workflow,
  open,
  onOpenChange,
  onSuccess,
}: ScheduleWorkflowDialogProps) {
  const { t } = useTranslation();
  const [cronExpression, setCronExpression] = useState('0 18 * * *');
  const [isSubmitting, setIsSubmitting] = useState(false);

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      setCronExpression('0 18 * * *');
      setIsSubmitting(false);
    }
    onOpenChange(nextOpen);
  }

  async function handleSubmit() {
    if (!workflow || !cronExpression.trim()) return;
    setIsSubmitting(true);
    try {
      await api.post(`/workflows/${workflow.id}/schedule`, {
        cronExpression,
        parameters: {},
      });
      toast.success(t('integration.flow.schedule.saved'));
      handleOpenChange(false);
      onSuccess();
    } catch {
      toast.error(t('integration.flow.schedule.error'));
    } finally {
      setIsSubmitting(false);
    }
  }

  if (!workflow) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md" data-testid="flow-schedule-dialog">
        <DialogHeader>
          <DialogTitle>
            {t('integration.flow.schedule.title')}: {workflow.name}
          </DialogTitle>
        </DialogHeader>

        <CronPicker value={cronExpression} onChange={setCronExpression} />

        <DialogFooter>
          <Button
            onClick={() => void handleSubmit()}
            disabled={!cronExpression.trim() || isSubmitting}
            data-testid="flow-schedule-submit-btn"
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('integration.flow.schedule.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
